export type MenuCtaTrackingProperties = {
  location: string;
  label: string;
  cta_text?: string;
  destination_url?: string;
  is_external?: boolean;
  platform?: string;
  location_slug?: string;
  location_id?: string;
  order_provider?: string;
};

type MenuCtaTrackingInput = {
  location: string;
  label: string;
  ctaText?: string | null;
  destinationUrl?: string;
  currentUrl: string;
  locationSlug?: string;
  locationId?: string;
  orderProvider?: string;
};

function getDestinationPlatform(url: URL) {
  if (url.protocol === "tel:") return "phone";
  if (url.protocol === "mailto:") return "email";

  const hostname = url.hostname.toLowerCase().replace(/^www\./, "");
  if (!hostname) return undefined;
  if (hostname.includes("grubhub")) return "grubhub";
  if (hostname.includes("toasttab")) return "toast";
  if (hostname.includes("opentable")) return "opentable";
  if (hostname.includes("yelp")) return "yelp";
  if (hostname.includes("google.")) return "google_maps";

  return hostname;
}

function normalizePublicHostname(hostname: string) {
  return hostname.toLowerCase().replace(/^www\./, "");
}

/**
 * Build one consistent, non-PII property bundle for delegated menu CTA clicks.
 * Keeping URL classification pure makes the external-order and reservation
 * exit behavior testable without a browser DOM.
 */
export function buildMenuCtaTrackingProperties({
  location,
  label,
  ctaText,
  destinationUrl,
  currentUrl,
  locationSlug,
  locationId,
  orderProvider,
}: MenuCtaTrackingInput): MenuCtaTrackingProperties {
  const properties: MenuCtaTrackingProperties = {
    location,
    label,
    cta_text: ctaText?.trim().replace(/\s+/g, " ") || undefined,
    location_slug: locationSlug,
    location_id: locationId,
    order_provider: orderProvider,
  };

  if (!destinationUrl) return properties;

  try {
    const current = new URL(currentUrl);
    const destination = new URL(destinationUrl, current);
    const isHttp = destination.protocol === "http:" || destination.protocol === "https:";

    return {
      ...properties,
      destination_url: destination.toString(),
      is_external: isHttp
        ? normalizePublicHostname(destination.hostname) !==
          normalizePublicHostname(current.hostname)
        : true,
      platform: getDestinationPlatform(destination),
    };
  } catch {
    return properties;
  }
}
